import { useEffect, useState } from 'react';
import { api, datum, menge, uhr } from '../api.ts';
import { Kopf, Karte, Leer } from '../teile/Geruest.tsx';
import { useFrisch } from '../teile/Frisch.tsx';

/**
 * Das Änderungsprotokoll.
 *
 * ORIGINALTEXT MARVIN (01.09.2026)
 *   „nochmal die aufforderung alle altlasten zu entfernen und ein sauberes
 *    system zu bauen!"
 *
 * BEIM NACHSEHEN, WAS DASTEHT UND NICHTS TUT, HERAUSGEKOMMEN. `GET /protokoll`
 * steht seit Migration 001 da und liefert, wer wann was geändert hat.
 * `protokoll.lesen` tragen drei Gruppen. **Gesehen hat es niemand: es gab
 * keinen Bildschirm.**
 *
 * WARUM DAS MEHR IST ALS EINE ANSICHT: an einem Dutzend Stellen beruft sich
 * dieses System auf „das Protokoll" - der gesetzte Pollercode steht darin,
 * das vom Innendienst gesetzte Passwort, die berichtigte Auftragsinfo, der
 * umgelegte Webhook, der gelöschte Konfigurationsschlüssel. **Eine Spur, die
 * niemand lesen kann, ist keine Spur, sondern eine Beruhigung.**
 *
 * UND ES IST NICHT DER SYSTEMZUSTAND. Der beantwortet „was ist gerade los",
 * dieser Bildschirm „was ist passiert". Zwei Fragen, und die zweite stellt
 * man mit einem Datum und einem Namen im Kopf - deshalb steht der Filter
 * oben und nicht in einem Klappmenü.
 */
export default function Protokoll() {
  const [d, setD] = useState<any>(null);
  const [laedt, setLaedt] = useState(false);
  const [f, setF] = useState<Record<string, string>>({});

  async function laden(mit: Record<string, string> = f) {
    setLaedt(true);
    try {
      const sauber = Object.fromEntries(
        Object.entries(mit).filter(([, v]) => String(v ?? '').trim() !== ''));
      setD(await api.protokoll(sauber));
    } finally { setLaedt(false); }
  }
  useEffect(() => { laden({}); }, []);
  useFrisch(laden);

  const setzen = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));
  const gefiltert = Object.values(f).some((v) => String(v ?? '').trim() !== '');

  return (
    <>
      <Kopf titel="Änderungsprotokoll"
            weg="Wer hat wann was geändert" />
      <div className="inhalt">

        <Karte titel="Suchen">
          <div className="leib">
            <div className="feldreihe">
              <label className="feldpaar">
                <span>Tabelle</span>
                <select value={f.tabelle ?? ''}
                        onChange={(e) => setzen('tabelle', e.target.value)}>
                  <option value="">alle</option>
                  {/* WORAUF SICH FILTERN LÄSST, KOMMT AUS DEN DATEN. Eine
                      feste Liste im Programmcode wäre eine zweite Wahrheit -
                      und sie stünde ausgerechnet auf dem Bildschirm, der
                      beweisen soll, was wirklich passiert ist. */}
                  {(d?.tabellen ?? []).map((t: any) => (
                    <option key={t.tabelle} value={t.tabelle}>
                      {t.tabelle} ({menge(t.n)})
                    </option>
                  ))}
                </select>
              </label>
              <label className="feldpaar">
                <span>von</span>
                <input type="date" value={f.von ?? ''}
                       onChange={(e) => setzen('von', e.target.value)} />
              </label>
              <label className="feldpaar">
                <span>bis</span>
                <input type="date" value={f.bis ?? ''}
                       onChange={(e) => setzen('bis', e.target.value)} />
              </label>
              <label className="feldpaar">
                <span>Suche</span>
                <input value={f.suche ?? ''} placeholder="Feld oder Wert"
                       onChange={(e) => setzen('suche', e.target.value)} />
              </label>
            </div>
            <div className="reihe" style={{ marginTop: 10 }}>
              <button className="knopf haupt" disabled={laedt}
                      onClick={() => laden()}>
                {laedt ? '…' : 'Suchen'}
              </button>
              {gefiltert && (
                <button className="knopf" disabled={laedt}
                        onClick={() => { setF({}); laden({}); }}>
                  Filter zurücknehmen
                </button>
              )}
            </div>
          </div>
        </Karte>

        <Karte titel="Was geändert wurde">
          {/* EIN DECKEL, DER SAGT, DASS ER GREIFT.
              „Ein Deckel, der nichts sagt, wenn er greift, ist eine Falle" -
              und bei einem Protokoll ist das die schlechteste Stelle dafür:
              wer nach einer Änderung sucht, die vor 201 Einträgen passiert
              ist, läse sonst „gibt es nicht". */}
          {d?.mehr && (
            <div className="hinweis warnung">
              <b>Es sind mehr.</b> Gezeigt werden die neuesten {menge(d.deckel)}.
              Grenzen Sie den Zeitraum ein oder suchen Sie nach einem Feld -
              sonst bleibt der Rest ausser Sicht, ohne dass es dasteht.
            </div>
          )}

          {!d ? null : !d.zeilen.length ? (
            <Leer text={gefiltert
              ? 'Zu dieser Suche gibt es keinen Eintrag.'
              : 'Es wurde noch nichts protokolliert.'} />
          ) : (
            <div className="leib">
              <table className="liste">
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>Wann</th>
                    <th style={{ width: 160 }}>Wer</th>
                    <th style={{ width: 140 }}>Woran</th>
                    <th style={{ width: 150 }}>Feld</th>
                    <th>Vorher</th>
                    <th>Nachher</th>
                  </tr>
                </thead>
                <tbody>
                  {d.zeilen.map((z: any) => (
                    <tr key={z.id} style={{ cursor: 'default' }}>
                      <td className="klein leise">
                        {datum(z.zeitpunkt)}
                        <div style={{ fontSize: 11 }}>{uhr(z.zeitpunkt)}</div>
                      </td>
                      <td className="klein">
                        {z.benutzer ?? <span className="leise">System</span>}
                        {/* ZWEI NAMEN IN EINER ZEILE. `im_namen_von` ist kein
                            Protokoll über das Einloggen, sondern die Antwort
                            auf „wer hat diese Menge eingetragen". */}
                        {z.im_namen_von && (
                          <div className="klein leise">
                            im Namen von {z.im_namen_von}
                          </div>
                        )}
                      </td>
                      <td className="klein">
                        <code>{z.tabelle}</code>
                        <div className="klein leise">{AKTION[z.aktion] ?? z.aktion}</div>
                      </td>
                      <td className="klein">{z.feld ?? '-'}</td>
                      <td className="klein leise">{kurz(z.wert_alt)}</td>
                      <td className="klein">{kurz(z.wert_neu)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="klein leise" style={{ marginBottom: 0 }}>
            Was geheim ist, steht hier nicht: ein Pollercode, ein Passwort, ein
            Bot-Token. <b>Ein Protokoll, das man lesen darf, ist kein Ort für
            einen Schlüssel.</b> Dass sie gesetzt wurden und von wem, steht
            sehr wohl darin.
          </p>
        </Karte>
      </div>
    </>
  );
}

const AKTION: Record<string, string> = {
  einfuegen: 'angelegt',
  aendern: 'geändert',
  loeschen: 'gelöscht',
};

/**
 * Ein langer Wert wird gekürzt - aber nur in der ANZEIGE und mit dem Rest im
 * Titel.
 *
 * Ein Mailtext oder ein Handbuchkapitel sprengt sonst die Zeile. Der ganze
 * Wert steht im `title`, damit nichts verlorengeht: **ein „und N weitere" ist
 * keine Auskunft, es sagt DASS etwas fehlt und nie WAS.**
 */
function kurz(w: unknown): any {
  const s = String(w ?? '');
  if (!s) return <span className="leise">-</span>;
  if (s.length <= 90) return s;
  return <span title={s}>{s.slice(0, 90)}…</span>;
}
